import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Grid, Link, Flex, Text } from "@chakra-ui/react";
import { LandscapeImage, PortraitImage, XpanImage } from "../ImageComponents";

const WorkPage = () => {
  const { t } = useTranslation('navigation');
  const navigate = useNavigate();
  const { lng } = useParams<{ lng: string }>();
  const currentLanguage = lng || 'en';

  const getLocalizedPath = (path: string) => {
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `/${currentLanguage}${cleanPath}`;
  };

  // Left column (Projects and People)
  const leftColumn = [
    {
      title: t('workSections.projects'),
      link: "/projects",
      imgSrc: "/images/projects.jpg",
      alt: "Film photography projects Costa del Sol - Interior design photography Marbella hotels and commercial spaces",
      component: LandscapeImage,
    },
    {
      title: t('workSections.people'),
      link: "/people",
      imgSrc: "/images/people.jpg",
      alt: "Portrait photography Costa del Sol - Professional photographer Marbella lifestyle and commercial photography",
      maxHeight: ["300px", "550px"],
      component: PortraitImage,
    },
  ];

  // Right column (Zines and Moments)
  const rightColumn = [
    {
      title: t('workSections.zines'),
      link: "/zines",
      imgSrc: "/images/zines.jpg",
      alt: "Photography zines and publications - Analog film photography collections",
      component: XpanImage,
    },
    {
      title: t('workSections.moments'),
      link: "/moments",
      imgSrc: "/images/moments.jpg",
      alt: "Lifestyle photography Costa del Sol - Commercial marketing imagery Marbella hotels and businesses",
      component: LandscapeImage,
    },
  ];

  return (
    <Box
      maxWidth="1400px" // Limit page width to align with header
      margin="0 auto"
      padding="20px"
      pr="5%"
      pl="5%"
    >

      {/* Hidden SEO content - visible to search engines but not users */}
      <Box 
        position="absolute" 
        left="-10000px" 
        width="1px" 
        height="1px" 
        overflow="hidden"
      >
        {currentLanguage === 'es' ? (
          <>
            <Text as="h1">Fotógrafo Analógico Costa del Sol</Text>
            <Text as="h2">Pablo Ledesma Fotografía Profesional Marbella</Text>
            <Text>Servicios profesionales de fotografía analógica para eventos, conciertos, retratos y proyectos de arte en Marbella, Málaga y Costa del Sol. Especializado en fotografía en película 35mm y formato medio usando película Kodak premium. Fotografía analógica auténtica que captura el alma de tu momento con intención y arte.</Text>
          </>
        ) : (
          <>
            <Text as="h1">Film Photographer Costa del Sol</Text>
            <Text as="h2">Pablo Ledesma Professional Photography Marbella</Text>
            <Text>Professional analog photography services for events, concerts, portraits and fine art projects in Marbella, Málaga and Costa del Sol. Specializing in 35mm and medium format film photography using premium Kodak film stock. Authentic film photography that captures the soul of your moment with intention and artistry.</Text>
          </>
        )}
      </Box>

      <Grid
        templateColumns={{ base: "1fr", md: "1fr 1fr" }} // Two columns for desktop
        // gap={10} // Consistent horizontal gap
        gap={5} // Consistent horizontal gap
      >
        {/* Left Column */}
        <Box>
          {leftColumn.map(({ title, link, imgSrc, alt, maxHeight, component: Component }, index) => (
            <Link
              key={index}
              onClick={() => navigate(getLocalizedPath(link))}
              cursor="pointer"
              _hover={{ textDecoration: "none" }}
              width="100%"
            >
              <Flex
                flexDirection="column"
                alignItems="center"
                width="100%"
                marginBottom={10} // Consistent vertical spacing between sections
              >
                {/* Render the Appropriate Image Component with Title */}
                <Component src={imgSrc} alt={alt} title={title} maxHeight={maxHeight}/>
              </Flex>
            </Link>
          ))}
        </Box>

        {/* Right Column */}
        <Box>
          {rightColumn.map(({ title, link, imgSrc, alt, component: Component }, index) => (
            <Link
              key={index}
              onClick={() => navigate(getLocalizedPath(link))}
              cursor="pointer"
              _hover={{ textDecoration: "none" }}
              width="100%"
            >
              <Flex
                flexDirection="column"
                alignItems="center"
                width="100%"
                marginBottom={10} // Consistent vertical spacing between sections
              >
                {/* Render the Appropriate Image Component with Title */}
                <Component src={imgSrc} alt={alt} title={title} />
              </Flex>
            </Link>
          ))}
        </Box>
      </Grid>
    </Box>
  );
};

export default WorkPage;
